import { useQuery } from '@tanstack/react-query'
import { Check, Pencil, Plus, Tags, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { Button, IconButton } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Skeleton } from '@/components/ui/Skeleton'
import { examTypesQuery, useSaveExamType } from '../api'
import type { ExamType } from '../types'

/** Small inline editor for exam types (Unit test, Midterm, Final…). */
export function ExamTypesDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('exams')
  const types = useQuery(examTypesQuery())
  const save = useSaveExamType()
  const [editing, setEditing] = useState<ExamType | 'new' | null>(null)
  const [name, setName] = useState('')

  const start = (type: ExamType | 'new') => {
    setEditing(type)
    setName(type === 'new' ? '' : type.name)
  }
  const submit = () => {
    if (!name.trim() || editing === null) return
    save.mutate(
      {
        id: editing === 'new' ? undefined : editing.id,
        name: name.trim(),
        description: editing === 'new' ? null : editing.description,
      },
      {
        onSuccess: () => {
          toast.success(t('Exam type saved'), name.trim())
          setEditing(null)
        },
        onError: (e) => toast.error(t('Could not save'), errorMessage(e)),
      },
    )
  }

  const editor = (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <Input
        size="sm"
        aria-label={t('Exam type name')}
        autoFocus
        value={name}
        maxLength={100}
        onChange={(e) => setName(e.target.value)}
      />
      <IconButton
        type="submit"
        label={t('Save')}
        icon={<Check />}
        size="icon-sm"
        variant="soft"
        loading={save.isPending}
      />
      <IconButton label={t('Cancel')} icon={<X />} size="icon-sm" onClick={() => setEditing(null)} />
    </form>
  )

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Tags />}
      title={t('Exam types')}
      description={t('Kinds of assessment used to group exams.')}
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('Done')}
        </Button>
      }
    >
      {types.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <ul className="divide-y divide-line rounded-control border border-line">
          {(types.data ?? []).map((type) => (
            <li key={type.id} className="flex items-center gap-2 px-3 py-2">
              {editing !== 'new' && editing?.id === type.id ? (
                <div className="flex-1">{editor}</div>
              ) : (
                <>
                  <span className="flex-1 text-sm text-ink">{type.name}</span>
                  <IconButton
                    label={t('Rename {{name}}', { name: type.name })}
                    icon={<Pencil />}
                    size="icon-sm"
                    onClick={() => start(type)}
                  />
                </>
              )}
            </li>
          ))}
          <li className="px-3 py-2">
            {editing === 'new' ? (
              editor
            ) : (
              <Button variant="link" size="sm" leftIcon={<Plus />} onClick={() => start('new')}>
                {t('Add a type')}
              </Button>
            )}
          </li>
        </ul>
      )}
    </Dialog>
  )
}
